import React, { useState, useEffect } from 'react';
import { Container, Paper, Box, TextField, Button, Typography, Alert, CircularProgress, InputAdornment, IconButton } from '@mui/material';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Visibility, VisibilityOff, LockReset, CheckCircle } from '@mui/icons-material';
import SelectivateLogo from '../components/SelectivateLogo';

const ResetPasswordPage = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
      setVerifying(false);
      return;
    }

    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await fetch('/api/auth/verify-reset-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      const data = await response.json();

      if (data.success) {
        setTokenValid(true);
        setUserInfo(data.user);
      } else {
        setError(data.message || 'Invalid or expired reset token.');
      }
    } catch (err) {
      setError('An error occurred while verifying the reset token.');
      console.error('Token verification error:', err);
    } finally {
      setVerifying(false);
    }
  };

  const validatePassword = (password) => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(password)) {
      return 'Password must contain at least one number';
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      return 'Password must contain at least one special character';
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    // Validate password
    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    // Check password confirmation
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, password }),
      });

      const data = await response.json();

      if (data.success) {
        setMessage(data.message);
        setResetSuccess(true);
      } else {
        setError(data.message || 'An error occurred. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Reset password error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 'calc(100vh - 80px)',
          }}
        >
          <CircularProgress size={48} sx={{ color: '#4CAF50' }} />
          <Typography variant="h6" sx={{ mt: 2, color: '#4CAF50' }}>
            Verifying reset token...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!tokenValid) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 'calc(100vh - 80px)',
          }}
        >
          <Paper elevation={4} sx={{ p: 4, maxWidth: 450, width: '100%', borderRadius: '16px', textAlign: 'center' }}>
            <Typography variant="h5" color="error" sx={{ mb: 2 }}>
              Invalid Reset Link
            </Typography>
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              The password reset link may have expired or is invalid. Please request a new password reset.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                component={Link}
                to="/forgot-password"
                variant="contained"
                sx={{ 
                  backgroundColor: '#4CAF50',
                  '&:hover': {
                    backgroundColor: '#45a049',
                  },
                }}
              >
                Request New Reset
              </Button>
              <Button
                component={Link}
                to="/login"
                variant="outlined"
                sx={{ 
                  borderColor: '#4CAF50',
                  color: '#4CAF50',
                  '&:hover': {
                    borderColor: '#45a049',
                    backgroundColor: 'rgba(76, 175, 80, 0.04)',
                  },
                }}
              >
                Back to Login
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 'calc(100vh - 80px)',
        }}
      >
        <Box
          sx={{ 
            mb: 4, 
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center', 
          }}
        >
          <Box 
            component="img" 
            src="/SELECCIÓN_logo_PNG.png"
            alt="SELECCIÓN Consulting"
            sx={{ 
              width: 200, 
              mb: 2
            }}
          />
          <Box sx={{ mb: 2 }}>
            <SelectivateLogo variant="banner" size="large" />
          </Box>
          <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 2 }}>
            Create a new password
          </Typography>
        </Box>
        
        <Paper 
          elevation={4} 
          sx={{ 
            p: 4, 
            maxWidth: 450, 
            width: '100%',
            borderRadius: '16px'
          }}
        >
          {!resetSuccess ? (
            <Box component="form" onSubmit={handleSubmit}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <LockReset sx={{ color: '#4CAF50', mr: 1 }} />
                <Typography variant="h5" color="#4CAF50" fontWeight="600">
                  Reset Password
                </Typography>
              </Box>
              
              {userInfo && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  Resetting password for: <strong>{userInfo.email}</strong>
                </Alert>
              )}

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <TextField
                label="New Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                margin="normal"
                required
                autoFocus
                sx={{ mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                helperText="Password must be at least 8 characters with uppercase, lowercase, number, and special character"
              />

              <TextField
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                fullWidth
                margin="normal"
                required
                sx={{ mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading || !password || !confirmPassword}
                sx={{ 
                  mt: 2, 
                  mb: 2,
                  height: 48,
                  backgroundColor: '#4CAF50',
                  '&:hover': {
                    backgroundColor: '#45a049',
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Reset Password'
                )}
              </Button>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                <CheckCircle sx={{ color: '#4CAF50', mr: 1, fontSize: 32 }} />
                <Typography variant="h5" color="#4CAF50" fontWeight="600">
                  Password Reset Successfully
                </Typography>
              </Box>
              
              <Alert severity="success" sx={{ mb: 3 }}>
                {message}
              </Alert>
              
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Your password has been updated. You can now sign in with your new password.
              </Typography>

              <Button
                component={Link}
                to="/login"
                variant="contained"
                fullWidth
                sx={{ 
                  backgroundColor: '#4CAF50',
                  '&:hover': {
                    backgroundColor: '#45a049',
                  },
                }}
              >
                Sign In
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default ResetPasswordPage;